'use client';

import { FilmScreening } from '@/app/[locale]/(rest)/films/[slug]/components/ScreeningList';
import { useDialogContext } from '@/contexts/DialogContext';
import { PathEnum } from '@/i18n/locale-config';
import { useRouter } from '@/i18n/navigation';
import useCartStore, { Ticket } from '@/store/cartStore';
import { createClient } from '@/utils/supabase/client';
import { cn } from '@/utils/tailwind-merge';
import { useLocale, useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import Image from '../Image';
import { AddToCartFilm } from '../ui/AddToCartButton';
import Button from '../ui/Button';
import CartItem from './CartItem';
import Screening from './Screening';

interface CartDialogProps {
    initialStep?: number;
    film?: AddToCartFilm;
}

const CartDialog = (props: CartDialogProps) => {
    const { initialStep = 1, film } = props;
    const t = useTranslations();
    const locale = useLocale();
    const router = useRouter();
    const supabase = createClient();

    const { dequeueDialog } = useDialogContext();
    const closeAuthDialog = () => dequeueDialog();

    const isPartOfShortProgramme = !!film?.short_programme;
    const isVirtualAvailable =
        film?.screenings?.some((screening) => screening.screening_type === 'ONLINE') ||
        film?.short_programme?.screenings?.some(
            (screening) => screening.screening_type === 'ONLINE',
        ) ||
        false;
    const isCinemaAvailable =
        film?.screenings?.some((screening) => screening.screening_type === 'OFFLINE') ||
        film?.short_programme?.screenings?.some(
            (screening) => screening.screening_type === 'OFFLINE',
        ) ||
        false;
    const [selectedTab, setSelectedTab] = useState<'virtual' | 'cinema'>(
        isVirtualAvailable ? 'virtual' : 'cinema',
    );
    const [step, setStep] = useState<number>(initialStep);
    const [passToUpgrade, setPassToUpgrade] = useState<Ticket | null>(null);
    const [tickets, setTickets] = useState<Ticket[]>([]);

    const { items, addItem, removeItem, increaseQuantity, decreaseQuantity } = useCartStore();

    const [isUpgradeSelected, setIsUpgradeSelected] = useState<boolean>(
        items.some((item) => item.ticket.id === passToUpgrade?.id) || false,
    );

    const decreaseQuantityPrevent = (ticketId: string, screeningId?: string) => {
        if (items.find((item) => item.ticket.id === passToUpgrade?.id)?.quantity === 1) {
            setIsUpgradeSelected(false);
        }

        decreaseQuantity(ticketId, screeningId);
    };

    const handleBack = () => {
        if (step === 1) closeAuthDialog();
        if (initialStep === 2) closeAuthDialog();
        else setStep(step - 1);
    };

    const handleContinue = () => {
        if (selectedTab === 'cinema') {
            setStep(step + 1);
        } else {
            let ticket: Ticket | undefined = tickets.find(
                (ticket) =>
                    ticket.usage_limits.single_use &&
                    ticket.usage_limits.online === 1 &&
                    ticket.usage_limits.in_cinema === 1,
            );

            if (isPartOfShortProgramme) {
                ticket = tickets.find(
                    (ticket) =>
                        ticket.usage_limits.single_use &&
                        ticket.usage_limits.online === 1 &&
                        ticket.usage_limits.in_cinema === 0,
                );
            }

            const filmOnlineScreenings = film?.screenings?.find(
                (screening) => screening.screening_type === 'ONLINE',
            );
            const shortProgrammeScreenings = film?.short_programme?.screenings?.find(
                (screening) => screening.screening_type === 'ONLINE',
            );

            const onlineScreenings = filmOnlineScreenings || shortProgrammeScreenings;

            if (!onlineScreenings) {
                console.error('No online screenings found');
                return;
            }

            if (!ticket) {
                console.error('No single-use tickets found');
                return;
            }

            addItem({ ticket, film, screening: onlineScreenings, quantity: 1 });
            setStep(2);
        }
    };

    const navigateToCheckout = () => {
        router.push(PathEnum.Checkout);
        dequeueDialog();
    };

    const selectPass = () => {
        setIsUpgradeSelected(true);
        addItem({
            ticket: passToUpgrade as Ticket,
            quantity: 1,
        });
    };

    const addToBasket = (screening: FilmScreening) => {
        const ticket = tickets.find(
            (ticket) =>
                ticket.usage_limits.single_use &&
                ticket.usage_limits.online === 1 &&
                ticket.usage_limits.in_cinema === 1,
        );

        if (!ticket) {
            console.error('No single-use tickets found');
            return;
        }

        addItem({ ticket, film, screening: screening, quantity: 1 });
    };

    const removeFromBasket = (ticketId: string) => {
        removeItem(ticketId);
    };

    const filmScreenings =
        film && film.screenings && film.screenings.length > 0
            ? film.screenings.filter((screening) => screening.screening_type === 'OFFLINE')
            : [];
    const filmShortProgrammeScreenings =
        film &&
        film.short_programme &&
        film.short_programme.screenings &&
        film.short_programme.screenings.length > 0
            ? film.short_programme?.screenings.filter(
                  (screening) => screening.screening_type === 'OFFLINE',
              )
            : [];
    const offlineScreenings = [...filmScreenings, ...filmShortProgrammeScreenings];

    const tabClasses = 'lg:text-[46px] text-md font-normal uppercase text-white/30';

    const isSingleUse = (ticket: Ticket) =>
        ticket.usage_limits.single_use && ticket.usage_limits.in_cinema > 0;

    const isUpgradeAvailable =
        passToUpgrade &&
        (items.length >= 2 || items.some((item) => isSingleUse(item.ticket) && item.quantity >= 2));

    useEffect(() => {
        const fetchTickets = async () => {
            const { data, error } = await supabase
                .from('tickets')
                .select('*')
                .or('purchase_location.eq.ONLINE,purchase_location.eq.BOTH')
                .returns<Ticket[]>();
            if (error) {
                console.error('Error fetching tickets:', error);
                return;
            }

            if (data) {
                setTickets(data);
                setPassToUpgrade(data.find((ticket) => ticket.usage_limits.in_cinema > 5) || null);
            }
        };

        fetchTickets();
    }, [supabase]);

    return (
        <div className="lg:px-12 px-3 py-5 bg-black border border-white flex flex-col max-h-[96vh]">
            <Button onClick={handleBack}>
                <>
                    <Image
                        className="mr-3 rotate-180"
                        src="/img/icons/arrow.svg"
                        alt="Arrow"
                        width={16}
                        height={16}
                    />
                    <span className="font-helvetica-neue font-light text-[12px] lg:text-p text-tofu">
                        {t('Common.back')}
                    </span>
                </>
            </Button>

            {step === 1 && (
                <>
                    <h5 className="text-h5 lg:text-[46px] font-normal uppercase text-white">
                        {t('Cart.chooseOption')}
                    </h5>
                    <div className="flex gap-8 lg:gap-12 py-5 border-t border-b border-white/50 mb-6 lg:mb-12">
                        {isVirtualAvailable && (
                            <button
                                className={cn(
                                    tabClasses,
                                    selectedTab === 'virtual' && 'text-white',
                                )}
                                onClick={() => setSelectedTab('virtual')}
                            >
                                {t('Cart.virtualExclusive')}
                            </button>
                        )}
                        {isCinemaAvailable && (
                            <button
                                className={cn(tabClasses, selectedTab === 'cinema' && 'text-white')}
                                onClick={() => setSelectedTab('cinema')}
                            >
                                {t('Cart.inCinema')}
                            </button>
                        )}
                    </div>
                    <div className="flex flex-col h-auto overflow-auto gap-7 max-lg:gap-5 md:pr-10">
                        {selectedTab === 'virtual'
                            ? null
                            : offlineScreenings?.map((screening, index) => (
                                  <Screening
                                      key={index}
                                      screening={screening}
                                      onAddToBasket={() => addToBasket(screening)}
                                      onRemoveFromBasket={() => removeFromBasket(screening.id)}
                                      isAddedInitially={items.some(
                                          (item) => item.screening?.id === screening.id,
                                      )}
                                      disabled={
                                          screening.id === '81704ae2-f2d0-47e9-be61-4901b57d6646'
                                      }
                                  />
                              ))}
                    </div>
                    {selectedTab !== 'virtual' && (
                        <div
                            className={`${offlineScreenings?.length > 1 ? 'shadow-[#000000]' : 'pb-10'} shadow-xl rotate-180 translate-y-1`}
                        >
                            &nbsp;
                        </div>
                    )}
                    {selectedTab === 'virtual' && (
                        <p className="mb-5 text-xl text-gray-200 uppercase">
                            {t('Cart.quebecOnly')}
                        </p>
                    )}
                    <Button
                        className="mt-auto max-sm:w-full text-black py-3"
                        onClick={handleContinue}
                        variant="secondary"
                        title={t('Common.continue')}
                        textClasses="font-trade-gothic font-normal"
                    />
                </>
            )}

            {step === 2 && (
                <>
                    <h5 className="text-h5 lg:text-[46px] font-normal uppercase border-b border-white/50 text-white">
                        {t('Cart.yourBasket')}
                    </h5>
                    <div className="flex flex-col h-auto overflow-auto gap-7 max-lg:gap-5 pr-10 max-lg:pr-5 mt-5">
                        {items.map((item, index) => (
                            <CartItem
                                key={index}
                                item={item}
                                increaseQuantity={() =>
                                    increaseQuantity(item.ticket.id, item.screening?.id)
                                }
                                decreaseQuantity={() =>
                                    decreaseQuantityPrevent(item.ticket.id, item.screening?.id)
                                }
                                removeItem={() => removeItem(item.ticket.id, item.screening?.id)}
                            />
                        ))}
                    </div>
                    <div className="shadow-[#000000] shadow-xl rotate-180 translate-y-1">
                        &nbsp;
                    </div>
                    {!isUpgradeSelected && passToUpgrade && isUpgradeAvailable && (
                        <div className="flex gap-10 justify-between items-center py-3 border-t xl:border-b border-white/50 max-xl:flex-col max-xl:gap-3 max-xl:items-start max-xl:py-3">
                            <h4 className="uppercase  text-xl lg:!text-[32px] leading-none text-white">
                                {t('Cart.maybePass')}
                            </h4>
                            <div className="flex items-center justify-between gap-10 max-xl:w-full">
                                <span className="text-sm md:text-[22px] lg:text-[28px] uppercase text-nowrap">
                                    {
                                        passToUpgrade.name?.[
                                            locale as keyof typeof passToUpgrade.name
                                        ]
                                    }
                                </span>
                                <span className="text-[22px] lg:text-[28px] uppercase text-nowrap">
                                    $ {passToUpgrade.price}
                                </span>
                            </div>
                            <Button
                                className="max-sm:w-full flex items-center px-5 py-2.5 border border-solid border-primary text-white [&>*]:pb-0"
                                onClick={selectPass}
                                variant="arrow"
                                title={t('Common.select')}
                                textClasses="font-trade-gothic font-normal text-white leading-none"
                            />
                        </div>
                    )}

                    <div className="mt-auto sm:hidden mb-5 flex justify-between gap-5 items-center">
                        <div className="flex flex-col gap-1 w-2/5">
                            <span className="text-p font-helvetica-neue font-light max-lg:text-[14px] text-nowrap text-gray-200 inline-block">
                                {t('Cart.totalPrice')}
                            </span>
                            <span className="font-bold text-[30px] lg:text-[46px] leading-none text-primary text-nowrap truncate line-clamp-1 inline-block">
                                ${' '}
                                {items.reduce(
                                    (total, item) => total + item.ticket.price * item.quantity,
                                    0,
                                )}
                            </span>
                        </div>
                        <span
                            className="text-lg md:text-[26px] uppercase text-gray-100 py-7 
                 max-xl:py-3 leading-none"
                        >
                            {t('Cart.payInCheckout')}
                        </span>
                    </div>
                    <span
                        className="hidden sm:inline-block text-lg md:text-[26px] uppercase text-gray-100 py-7 
                 max-xl:py-3 leading-none"
                    >
                        {t('Cart.payInCheckout')}
                    </span>
                    <div className="flex justify-between max-lg:flex-col gap-3 w-full">
                        <Button
                            className="max-sm:w-full border-primary text-white max-lg:hidden"
                            onClick={dequeueDialog}
                            variant="arrow"
                            title={t('Cart.continueShopping')}
                            textClasses="font-trade-gothic font-normal text-white"
                        />
                        <div className="flex gap-5 lg:gap-20 max-sm:flex-wrap max-lg:justify-between">
                            <div className="hidden sm:flex flex-col gap-1 max-sm:max-w-1/3 w-max truncate">
                                <span className="text-p font-helvetica-neue font-light max-lg:text-[14px] text-nowrap text-gray-200 inline-block">
                                    {t('Cart.totalPrice')}
                                </span>
                                <span className="font-bold text-[30px] lg:text-[46px] leading-none text-primary text-nowrap truncate line-clamp-1 inline-block">
                                    ${' '}
                                    {items.reduce(
                                        (total, item) => total + item.ticket.price * item.quantity,
                                        0,
                                    )}
                                </span>
                            </div>
                            <Button
                                id="add-to-basket-trigger"
                                className="max-sm:w-full text-black"
                                onClick={navigateToCheckout}
                                variant="secondary"
                                title={t('Cart.goToCheckout')}
                                textClasses="font-trade-gothic font-normal"
                            />
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default CartDialog;
